import { useMutation } from '@tanstack/react-query';
import { SourcingStageStat, SourcingStageType } from 'api-clients/monolith';
import { useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { updateStageMappings } from '@/api-clients/update-stage-mappings';
import { useToaster } from '@/components/base/toaster';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { DialogBody } from '@/components/ui/dialog';
import { Typography } from '@/components/ui/typography';
import { useOpeningSlug } from '@/hooks/use-opening-slug';
import { STAGE_TYPES } from '@/types/stage-types';

import { type Inputs, FormInputs } from './form-inputs';

interface StageMappingsFormProps {
  children: (state: {
    isSaving: boolean;
    isFormDirty: boolean;
  }) => React.ReactNode;
  funnelData: SourcingStageStat[];
  refetchFunnelData?: () => Promise<void>;
  hiringGoalConversionRate: number | null;
  setIsModalOpen: (isModalOpen: boolean) => void;
}

export function StageMappingsForm({
  children,
  funnelData,
  refetchFunnelData,
  hiringGoalConversionRate,
  setIsModalOpen,
}: StageMappingsFormProps) {
  const intl = useIntl();
  const openingSlug = useOpeningSlug();
  const { notifySuccess, notifyError } = useToaster();
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Set the form default values from the /sourcing_stage_stats API response
  const defaultValues = useMemo(() => {
    return funnelData.reduce(
      (acc, stage) => ({
        ...acc,
        [stage.stage_type]: {
          hireStageId: stage.hire_stage.id,
          expectedConversion: stage.conversion_stats.expected
            ? stage.conversion_stats.expected
            : null,
        },
      }),
      {
        landing: {
          expectedConversion: hiringGoalConversionRate,
        },
      } as Inputs,
    );
  }, [funnelData, hiringGoalConversionRate]);

  const hookForm = useForm<Inputs>({
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { dirtyFields },
    getValues,
  } = hookForm;

  const { mutate: updateStageMappingsMutation, isPending } = useMutation({
    mutationFn: updateStageMappings,
    onSuccess: async () => {
      notifySuccess({
        message: intl.formatMessage({
          defaultMessage: 'Stage mappings updated successfully!',
          description:
            'Success toast after saving funnel / workflow stage links',
        }),
      });
      await refetchFunnelData?.();
      setIsModalOpen(false);
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to update stage mappings!',
          description: 'Error toast when stage mapping save API fails',
        }),
      });
    },
  });

  const handleConfirm = () => {
    const changedFields = Object.entries(getValues())
      .filter(
        ([stageType]) =>
          stageType === 'hired' || Object.keys(dirtyFields).includes(stageType),
      )
      .map(([stageType, stageData]) => {
        return {
          stage_type: stageType as SourcingStageType,
          title: STAGE_TYPES[stageType as SourcingStageType],
          stage_id: stageData.hireStageId,
          expected_conversion: stageData.expectedConversion,
          is_automated: stageType === 'landing' || stageType === 'hired', // This is a temporary fix until we provide the option to the user to select if the stage is automated or not
        };
      });

    updateStageMappingsMutation({
      openingSlug,
      requestBody: {
        stages: changedFields,
      },
    });

    setConfirmOpen(false);
  };

  const onSubmit: SubmitHandler<Inputs> = () => {
    setConfirmOpen(true);
  };

  return (
    <>
      <form
        className="flex min-h-0 flex-1 flex-col"
        onSubmit={handleSubmit(onSubmit)}
      >
        <DialogBody className="flex flex-col gap-4 p-6">
          <Typography variant="body1" className="!text-fx-neutral-600">
            <FormattedMessage
              defaultMessage="Select which workflow stages to track within your sourcing funnel. The predicted conversion is based on past data and shows the conversion percent from each stage to the “Hire” stage."
              description="Help text at top of map funnel stages modal: explains what each column is for"
            />
          </Typography>

          <div className="grid grid-cols-[1fr_24px_2fr_1.1fr] items-center gap-6 px-4">
            <Typography variant="subtitle2">
              <FormattedMessage
                defaultMessage="Funnel stage"
                description="Table header: marketing/sourcing step name in the funnel"
              />
            </Typography>
            <span />
            <Typography variant="subtitle2">
              <FormattedMessage
                defaultMessage="Workflow stage"
                description="Table header: which ATS/workflow step maps to the funnel step"
              />
            </Typography>
            <Typography variant="subtitle2">
              <FormattedMessage
                defaultMessage="Predicted conversion"
                description="Table header: forecast percent to hire from that funnel stage"
              />
            </Typography>
          </div>

          {(Object.entries(STAGE_TYPES) as [SourcingStageType, string][]).map(
            ([stageType, stageName]) => (
              <FormInputs
                key={stageType}
                stageType={stageType}
                stageName={stageName}
                hookForm={hookForm}
              />
            ),
          )}
        </DialogBody>
        {children({
          isSaving: isPending,
          isFormDirty: Object.keys(dirtyFields).length > 0,
        })}
      </form>

      <ConfirmationDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={intl.formatMessage({
          defaultMessage: 'Confirm stage mappings update',
          description:
            'Dialog title before saving changes that affect all org funnels',
        })}
        description={intl.formatMessage({
          defaultMessage:
            'Updating stage mappings will update all funnels in the workflow',
          description:
            'Dialog body: warn that the change is organization-wide, not one opening only',
        })}
        confirmLabel={intl.formatMessage({
          defaultMessage: 'Confirm',
          description: 'Primary button: save stage mapping changes',
        })}
        cancelLabel={intl.formatMessage({
          defaultMessage: 'Cancel',
          description: 'Secondary button: go back without saving mappings',
        })}
        onConfirm={handleConfirm}
      />
    </>
  );
}
